import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Container,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import { 
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  ExitToApp as ExitToAppIcon,
  AccountCircle as AccountCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;

const Layout = () => {
  const theme = useTheme();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isMenuOpen = Boolean(anchorEl);
  
  // Efeito para mostrar/ocultar o indicador de carregamento durante a navegação
  useEffect(() => {
    // Simular carregamento ao mudar de rota
    const handleRouteChange = () => {
      setIsLoading(true);
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    };

    handleRouteChange();
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/acesso-interno');
    handleMenuClose();
  };

  const menuId = 'user-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id={menuId}
      keepMounted
      open={isMenuOpen}
      onClose={handleMenuClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        elevation: 1,
        sx: {
          mt: 1.5,
          minWidth: 200,
          borderRadius: 1,
          overflow: 'visible',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
          },
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {currentUser?.name || 'Usuário'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {currentUser?.email || ''}
        </Typography>
      </Box>
      
      <MenuItem 
        onClick={() => { 
          navigate('/perfil'); 
          handleMenuClose(); 
        }}
        sx={{
          py: 1.5,
          '&:hover': {
            backgroundColor: (theme) => 
              theme.palette.mode === 'light' 
                ? 'rgba(0, 0, 0, 0.04)' 
                : 'rgba(255, 255, 255, 0.08)',
          },
        }}
      >
        <AccountCircleIcon fontSize="small" sx={{ mr: 1.5, opacity: 0.75 }} />
        <span>Meu Perfil</span>
      </MenuItem>
      
      <Divider />
      
      <MenuItem 
        onClick={handleLogout}
        sx={{
          py: 1.5,
          color: (theme) => theme.palette.error.main,
          '&:hover': {
            backgroundColor: (theme) => 
              theme.palette.mode === 'light' 
                ? 'rgba(244, 67, 54, 0.08)' 
                : 'rgba(239, 83, 80, 0.16)',
          },
        }}
      >
        <ExitToAppIcon fontSize="small" sx={{ mr: 1.5, opacity: 0.75 }} />
        <span>Sair</span>
      </MenuItem>
    </Menu>
  );

  // Bypass desativado em produção para não renderizar layout sem usuário
  const bypassAuth = false;

  // Se não houver usuário e não estiver em bypass, não renderiza o layout
  if (!currentUser && !bypassAuth) {
    return null;
  }

  // Obtém a primeira letra do nome do usuário para o avatar
  const userInitial = (currentUser?.nome || currentUser?.name || 'U')?.charAt(0)?.toUpperCase();

  // Função para obter o título da página com base na rota
  const getPageTitle = (pathname) => {
    const pathMap = {
      '/dashboard': 'Negociações',
      '/perfil': 'Meu Perfil'
    };
    
    // Verifica se a rota exata existe no mapa
    if (pathMap[pathname]) {
      return pathMap[pathname];
    }
    
    return 'Hadritech';
  };

  const drawer = (
    <Box 
      sx={{ 
        width: drawerWidth, 
        height: '100vh',
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: (theme) =>
          theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.background.default,
      }}
    >
      {/* Cabeçalho do menu */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          minHeight: '64px',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography 
          variant="h6" 
          noWrap 
          component="div" 
          sx={{ 
            fontWeight: 600,
            background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          HADRITECH
        </Typography>
        <IconButton 
          onClick={handleDrawerToggle}
          sx={{ display: { md: 'none' } }}
        >
          {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      
      {/* Conteúdo do menu */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
        <List>
          <ListItemButton 
            component={Link} 
            to="/dashboard"
            selected={location.pathname === '/dashboard'}
            sx={{
              mx: 1,
              my: 0.5,
              borderRadius: 1,
              '&.Mui-selected': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(25, 118, 210, 0.08)'
                    : 'rgba(144, 202, 249, 0.16)',
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(25, 118, 210, 0.12)'
                      : 'rgba(144, 202, 249, 0.24)',
                },
              },
            }}
          >
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Negociações" 
              primaryTypographyProps={{
                fontWeight: location.pathname === '/dashboard' ? 600 : 400,
              }}
            />
          </ListItemButton>

          <ListItemButton 
            component={Link} 
            to="/dashboard/configurador"
            selected={location.pathname.startsWith('/dashboard/configurador')}
            sx={{
              mx: 1,
              my: 0.5,
              borderRadius: 1,
              '&.Mui-selected': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(25, 118, 210, 0.08)'
                    : 'rgba(144, 202, 249, 0.16)',
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(25, 118, 210, 0.12)'
                      : 'rgba(144, 202, 249, 0.24)',
                },
              },
            }}
          >
            <ListItemIcon>
              <AssignmentIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Configurador" 
              primaryTypographyProps={{
                fontWeight: location.pathname.startsWith('/dashboard/configurador') ? 600 : 400,
              }}
            />
          </ListItemButton>
        </List>
      </Box>
      
      {/* Rodapé do menu */}
      <Box 
        sx={{ 
          p: 2, 
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          v1.0.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar 
        position="fixed" 
        elevation={1}
        sx={{ 
          backgroundColor: (theme) => 
            theme.palette.mode === 'light' 
              ? '#ffffff' 
              : theme.palette.background.paper,
          color: (theme) => 
            theme.palette.mode === 'light' 
              ? theme.palette.text.primary 
              : '#ffffff',
          backdropFilter: 'blur(20px)',
          backgroundImage: 'none',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar disableGutters sx={{ px: { xs: 2, sm: 3 }, minHeight: '64px' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              color: 'inherit',
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600,
              fontSize: '1.25rem',
              background: (theme) => 
                theme.palette.mode === 'light'
                  ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                  : 'none',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
            }}
          >
            {getPageTitle(location.pathname)}
          </Typography>

          {/* Menu de perfil do usuário */}
          {currentUser ? (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <IconButton
                size="medium"
                edge="end"
                aria-label="conta do usuário atual"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{
                  p: 0.5,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 36, 
                    height: 36,
                    bgcolor: (theme) => theme.palette.primary.main,
                    color: (theme) => theme.palette.primary.contrastText,
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  {userInitial}
                </Avatar>
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="text" 
                component={Link} 
                to="/acesso-interno"
                sx={{
                  color: 'inherit',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                Entrar
              </Button>
              <Button 
                variant="contained"
                component={Link} 
                to="/register"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: (theme) => theme.shadows[4],
                  },
                }}
              >
                Cadastrar
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      {renderMenu}
      
      {/* Menu lateral */}
      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
        }}
        aria-label="menu de navegação"
      >
        {/* Menu mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Melhora o desempenho em mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              height: '100vh',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '0.4em',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' 
                    ? theme.palette.grey[200] 
                    : theme.palette.grey[900],
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? theme.palette.grey[400]
                    : theme.palette.grey[600],
                borderRadius: 4,
              },
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Menu desktop */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? theme.palette.grey[100]
                  : theme.palette.background.default,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '0.4em',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' 
                    ? theme.palette.grey[200] 
                    : theme.palette.grey[900],
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? theme.palette.grey[400]
                    : theme.palette.grey[600],
                borderRadius: 4,
              },
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      {/* Conteúdo principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: '64px',
          p: 3,
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[50]
              : theme.palette.background.default,
          minHeight: 'calc(100vh - 64px)',
          position: 'relative',
          overflowY: 'auto',
          overflowX: 'hidden',
          minWidth: 0,
        }}
      >
        {/* Overlay de carregamento (renderização condicional) */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1200,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 3,
                borderRadius: 1,
                backgroundColor: 'background.paper',
                boxShadow: 1,
              }}
            >
              <CircularProgress size={24} sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Carregando...
              </Typography>
            </Box>
          </Box>
        )}
        
        <Box 
          sx={{ 
            py: 2,
            width: '100%',
          }}
        >
          <Outlet />
        </Box>
      </Box>
      
      {/* Rodapé */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          mt: 'auto',
        }}
      >
        <Container maxWidth={false}>
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} Hadritech. Todos os direitos reservados.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
